import React, { useState } from 'react';
import { User, LogOut, BookMarked, MessageCircle as MessageCircleQuestion, Crown, Calculator, QrCode } from 'lucide-react';
import { useSubscription } from '../subscription/SubscriptionContext';
import { signOut } from '../../lib/auth';
import { ProfilePage } from '../profile/ProfilePage';
import { NotificationBell } from '../notifications/NotificationBell';
import { CalculatorModal } from '../calculators/CalculatorModal';
import { isWebViewGold, shouldBypassSubscription } from '../../lib/platform';
import BreadflowIcon from './BreadflowIcon.tsx';

interface HeaderProps {
  user: any | null;
  onAuthClick: () => void;
  onShowSubscriptionModal: () => void;
  onQRScan: () => void;
}

export function Header({ user, onAuthClick, onShowSubscriptionModal, onQRScan }: HeaderProps) {
  const { isSubscribed } = useSubscription();
  const [showProfile, setShowProfile] = useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);

  // Hide premium UI elements on WebViewGold iOS and Safari iOS/iPad (App Store compliance)
  const showPremiumUI = !shouldBypassSubscription() && !isSubscribed;

  const handleCalculatorClick = () => {
    if (isSubscribed) {
      setShowCalculatorModal(true);
    } else {
      onShowSubscriptionModal();
    }
  };

  return (
    <>
      <header className="bg-white shadow-sm px-4 py-3 flex items-center justify-between fixed top-0 left-0 right-0 h-16 z-50">
        <div className="flex flex-col items-center leading-tight">
          <h1 className="text-2xl text-gray-800 font-playfair">.Breadlify</h1>
        </div>
        <div className="flex items-center gap-3 sm:gap-6">
          {user ? (
            <>
              {isWebViewGold() && (
                <button
                  onClick={onQRScan}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl"
                  title="Scan QR Code"
                >
                  <QrCode className="w-5 h-5" />
                </button>
              )}
              <div className="relative">
                <button
                  onClick={handleCalculatorClick}
                  className={`p-2 text-blue-700 border border-blue-700 hover:bg-white hover:border hover:border-blue-700 rounded-xl ${showPremiumUI ? 'opacity-80' : ''}`}
                  title="Sourdough Calculators"
                >
                  <Calculator className="w-5 h-5" />
                </button>
                {showPremiumUI && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center p-0.5">
                    <Crown className="w-2.5 h-2.5 text-white fill-white" />
                  </div>
                )}
              </div>
              <NotificationBell/>
              <button
                onClick={() => setShowProfile(true)}
                className="p-2 text-gray-600 hover:bg-white rounded-xl"
                title="Profile"
              >
                <User className="w-5 h-5" />
              </button>
            </>
          ) : (
            <button
              onClick={onAuthClick}
              className="flex items-center gap-2 px-4 py-2 text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50"
            >
              <User className="w-4 h-4" />
              Log In
            </button>
          )}
        </div>
      </header>

      {showProfile && user && (
        <ProfilePage
          user={user}
          onClose={() => setShowProfile(false)}
          onUpdate={() => {
            // Force a re-render when profile is updated
            window.location.reload();
          }}
        />
      )}

      <CalculatorModal
        isOpen={showCalculatorModal}
        onClose={() => setShowCalculatorModal(false)}
      />
    </>
  );
}